<template>
    <!-- Renderless: this component exists only to own the generation session plumbing below. -->
    <div class="hidden" />
</template>

<script setup lang="ts">
import { useAiCreditsRefresh } from '@/composables/ai-upload/useAiCreditsRefresh';
import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import { useAiCustomerAuth } from '@/composables/ai-upload/useAiCustomerAuth';
import { useAiGalleryRestoreSnapshot } from '@/composables/ai-upload/useAiGalleryRestoreSnapshot';
import { rememberAiPersonalizationDefaults } from '@/composables/ai-upload/useAiPersonalizationDefaults';
import { useAiImageGenerationChannel } from '@/composables/ai-upload/useAiImageGenerationChannel';
import { useBeautyShotPolling } from '@/composables/ai-upload/useBeautyShotPolling';
import { useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import type { AppPageProps } from '@/types';
import type { ConfigurableProductPageObject } from '@/types/product';
import { usePage } from '@inertiajs/vue3';
import { computed, watch } from 'vue';

/**
 * Everything here used to live inside AiUploadButton, which the page mounted once per breakpoint —
 * so the Echo subscription, the beauty-shot poll and the credits watchers all ran twice. The
 * button is now rendered for a single breakpoint at a time, which also means it can unmount when
 * the viewport crosses `md`; these watchers must outlive that, so they hang off the page instead.
 */
type AiGenerationHostPageProps = AppPageProps & {
    product: ConfigurableProductPageObject;
    ai_credits_remaining?: {
        remaining: number;
        max: number;
        isDailyThresholdExceeded: boolean;
        isUserDailyLimitExceeded: boolean;
        hoursUntilNextCredit: number | null;
    } | null;
};

const page = usePage<AiGenerationHostPageProps>();
const session = useAiImageGenerationSessionStore();
const { isCustomerAuthenticated } = useAiCustomerAuth();
const { refreshCreditsRemaining } = useAiCreditsRefresh();
const { creditsRemaining, maxCredits, isDailyThresholdExceeded, isUserDailyLimitExceeded, hoursUntilNextCredit, pendingCredits } =
    useAiCreditsRemaining();

// Seed from the SSR page prop so both values are visible immediately on first
// render without waiting for the async fetch.
//
// Re-seed the composable whenever we navigate to a new CP page. Using a watcher
// rather than a one-time synchronous check because the module-level refs survive
// Inertia navigations. Inertia::once() re-evaluates the prop on every full page
// visit (new URL → new server request), so a changed prop reliably signals that
// we are now on a different CP and the composable state should be reset.
//
// If the session is active and no beauty shot has completed yet, the current
// generation already consumed one credit from the rate limiter but the user
// hasn't seen the result — mirror the non-refresh flow: display remaining+1
// now and defer the real count to pendingCredits so the gate watcher below
// applies it when the first beauty shot arrives.
watch(
    () => page.props.ai_credits_remaining,
    prop => {
        if (!prop) {
            return;
        }

        isDailyThresholdExceeded.value = prop.isDailyThresholdExceeded;
        isUserDailyLimitExceeded.value = prop.isUserDailyLimitExceeded;
        hoursUntilNextCredit.value = prop.hoursUntilNextCredit ?? undefined;
        maxCredits.value = prop.max;

        if (session.isActive && !session.isRevealGateOpen) {
            creditsRemaining.value = prop.remaining + 1;
            pendingCredits.value = prop.remaining;
            return;
        }

        creditsRemaining.value = prop.remaining;
    },
    { immediate: true },
);

// Apply the decremented count only once the first beauty shot is actually shown,
// so the badge doesn't update before the user sees the new result.
// Handles both the upload flow (AiUploadButton sets pendingCredits before activating)
// and the regenerate flow (AiRegenerateBlock sets pendingCredits but unmounts
// before its own watcher can fire).
watch(
    () => session.isRevealGateOpen,
    gateOpen => {
        if (!gateOpen || pendingCredits.value === undefined) {
            return;
        }

        creditsRemaining.value = pendingCredits.value;
        pendingCredits.value = undefined;
    },
);

// Re-fetch when the customer completes the login modal mid-session so the count
// reflects their personal limit rather than the guest default.
watch(isCustomerAuthenticated, authenticated => {
    if (!authenticated) {
        return;
    }

    void refreshCreditsRemaining();
});

// Keep the channel poll running after 'completed' until all beauty shot slots are
// discovered and settled. Beauty shot jobs are queued, so records may be created
// after the main 'completed' event fires — Reverb events for undiscovered slots
// are silently dropped, so repeated polls are the only way to find them.
const hasUnsettledBeautyShots = computed<boolean>(() => {
    if (session.beautyShots.length === 0) {
        return true;
    }

    return session.beautyShots.some(slot => slot.step !== 'completed' && slot.step !== 'failed');
});

useAiImageGenerationChannel(
    computed(() => session.correlationId),
    computed(() => session.token),
    update => session.applyStatusUpdate(update),
    (shotCorrelationId, update) => session.applyBeautyShotStatusUpdate({ correlationId: shotCorrelationId, ...update }),
    hasUnsettledBeautyShots,
);

useBeautyShotPolling();

// What the gallery is showing, kept where a script can read it before the page paints.
useAiGalleryRestoreSnapshot();

// And what a new text line starts as, so the next visit seeds its placeholder in the synced face
// rather than in Roboto until the deferred group lands.
rememberAiPersonalizationDefaults();
</script>
